import { Reducer } from 'redux';

import {
  GET_USER_ANSWERS_SUCCESS,
  GET_USER_BY_ID_SUCCESS,
  GET_USER_QUESTIONS_IMPROVE_SUCCESS,
  GET_USER_NEWS_SUCCESS,
  GET_USER_QUESTIONS_SUCCESS,
  GET_USER_TOPICS_SUCCESS,
  USER_PROFILE_ANY_FAILED,
  USER_PROFILE_ANY_REQUEST,
  GetUserSurveys,
  CopyUserSurvey,
  DeleteUserSurvey,
  GetUserTeam,
  GetDirectFeedback,
} from '~src/modules/reports/userProfile/constants/actions';
import { ThunkFailAction, ThunkSuccessAction } from '~src/services/apiMiddleware';
import {
  defaultInitState,
  resultListToObject,
  ProfileState,
} from '~src/modules/reports/shared/distributers/actionDistributor';
import { ReportSubjectTypes } from '~src/modules/reports/interfaces/header.interface';
import { User } from '~src/interfaces/user.interface';
import { PaginationI } from '~src/interfaces/pagination.interface';
import { SortingRespI } from '~src/interfaces/sorting.interface';
import { SurveyExtended, Survey } from '~src/modules/surveys/interfaces';
import { DirectFeedbackRespI, DirectFeedbackResponse } from '~src/modules/reports/interfaces/direct.interface';
import { mapIdToData } from '~src/utils';
import lStorage from '~src/services/localStorage.service';
import { LocalStorageKeys } from '~src/interfaces/localSotrage.types';
import { fetchExports } from '~src/store/exports/action.types';
import { GetSubjectNotes } from '~src/modules/reports/shared/components/notes/constants/actions';

export interface TeamMemberI {
  id: string;
  type: string;
  meta: {
    name: string;
    email: string;
    lastname: string;
    firstname: string;
    manager_uuid: string;
  };
  image_url: string;
}

interface ProfileOptions {
  auth_tenant_id: string | null;
  auth_user_id: string | null;
  cycle_id: string | null;
  group_id: string | null;
  question_id: string | null;
  survey_id: string;
  topic_id: string | null;
  url_values: any;
  user_id: string | null;
}

export interface ManagerTeamRespI {
  result: {
    direct_reports_list: TeamMemberI[];
  };
  pagination: PaginationI;
  sorting: SortingRespI;
  options: ProfileOptions;
}

export interface UserSurveysRespI {
  result: {
    survey_list: SurveyExtended[];
  };
  pagination: PaginationI;
  sorting: SortingRespI;
  options: ProfileOptions;
}

export interface UserProfileState extends ProfileState {
  direct: DirectFeedbackResponse;
  currentUser: User | null;
  loadingTeam: boolean;
  loadingDirect: boolean;
  managerTeam: ManagerTeamRespI | null;
  loadingSurveys: boolean;
  userSurveys: UserSurveysRespI | null;
  loadingCopy: boolean;
  copiedSurvey: Survey | null;
  loadingDel: boolean;
}

export function getInitialUserProfileState(): UserProfileState {
  return {
    ...defaultInitState,
    direct: {
      result: null,
      pagination: null,
    },
    loadingDirect: false,
    currentUser: null,
    loadingTeam: false,
    managerTeam: null,
    loadingSurveys: false,
    userSurveys: null,
    loadingCopy: false,
    copiedSurvey: null,
    loadingDel: false,
  };
}

const deleteSurvey = (
  userSurveys: UserSurveysRespI | null,
  { id }: ThunkSuccessAction,
): UserSurveysRespI | null => {
  if (!userSurveys) {
    return userSurveys;
  }
  const surveys = [...userSurveys.result.survey_list];
  const pagination = { ...userSurveys.pagination };
  const deletedIndex = surveys.findIndex((item) => item.id === id);
  lStorage.removeItem(
    LocalStorageKeys.OneTimeNotifications,
    `${fetchExports.Success}.${surveys[deletedIndex].id}`,
  );
  surveys.splice(deletedIndex, 1);
  pagination.count -= 1;
  pagination.total -= 1;
  return { ...userSurveys, result: { survey_list: surveys }, pagination };
};

const convertDirectFeedback = (data: DirectFeedbackRespI) => ({
  result: data.items ? mapIdToData(data.items) : null,
  pagination: data.pagination,
});

const userProfileReducer: Reducer<UserProfileState, ThunkSuccessAction | ThunkFailAction> = (
  state: UserProfileState = getInitialUserProfileState(),
  action,
) => {
  switch (action.type) {
    case USER_PROFILE_ANY_REQUEST: {
      return { ...state, loading: true };
    }
    case USER_PROFILE_ANY_FAILED: {
      return { ...state, loading: false };
    }
    case GET_USER_BY_ID_SUCCESS: {
      return { ...state, loading: false, currentUser: action.response.data };
    }
    case GET_USER_TOPICS_SUCCESS: {
      return {
        ...state,
        loading: false,
        topics: resultListToObject(ReportSubjectTypes.Topic, action.response.data),
      };
    }
    case GET_USER_QUESTIONS_SUCCESS: {
      return {
        ...state,
        loading: false,
        questions: resultListToObject(ReportSubjectTypes.Question, action.response.data),
      };
    }
    case GET_USER_QUESTIONS_IMPROVE_SUCCESS: {
      return {
        ...state,
        loading: false,
        questions_improve: resultListToObject(ReportSubjectTypes.QuestionsImprove, action.response.data),
      };
    }
    case GET_USER_ANSWERS_SUCCESS: {
      return {
        ...state,
        loading: false,
        answers: resultListToObject(ReportSubjectTypes.Answer, action.response.data),
      };
    }
    case GET_USER_NEWS_SUCCESS: {
      return {
        ...state,
        loading: false,
        news: resultListToObject(ReportSubjectTypes.News, action.response.data),
      };
    }
    case GetSubjectNotes.Success: {
      return {
        ...state,
        loading: false,
        notes: action.response.data,
      };
    }
    case GetUserTeam.Request:
      return {
        ...state,
        loadingTeam: true,
      };

    case GetUserTeam.Success:
      return {
        ...state,
        loadingTeam: false,
        managerTeam: action.response.data,
      };

    case GetUserTeam.Failed:
      return {
        ...state,
        loadingTeam: false,
      };
    case GetDirectFeedback.Request:
      return {
        ...state,
        loadingDirect: true,
      };

    case GetDirectFeedback.Success:
      return {
        ...state,
        loadingDirect: false,
        direct: convertDirectFeedback(action.response.data),
      };

    case GetDirectFeedback.Failed:
      return {
        ...state,
        loadingDirect: false,
      };

    case GetUserSurveys.Request:
      return {
        ...state,
        loadingSurveys: true,
      };

    case GetUserSurveys.Success:
      return {
        ...state,
        loadingSurveys: false,
        userSurveys: action.response.data,
      };

    case GetUserSurveys.Failed:
      return {
        ...state,
        loadingSurveys: false,
      };

    case CopyUserSurvey.Request:
      return {
        ...state,
        loadingCopy: true,
      };

    case CopyUserSurvey.Success:
      return {
        ...state,
        loadingCopy: false,
        copiedSurvey: action.response.data,
      };

    case CopyUserSurvey.Failed:
      return {
        ...state,
        loadingCopy: false,
      };

    case DeleteUserSurvey.Request:
      return {
        ...state,
        loadingDel: true,
      };

    case DeleteUserSurvey.Success:
      return {
        ...state,
        userSurveys: deleteSurvey(state.userSurveys, action as ThunkSuccessAction),
        loadingDel: false,
      };

    case DeleteUserSurvey.Failed:
      return {
        ...state,
        loadingDel: false,
      };

    default:
      return state;
  }
};

export default userProfileReducer;
